import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect } from "react";

import { useLocation, useNavigate, Link } from "react-router-dom";

import { TIMELINE_ICON } from "../../constants/icons";

import { httpRequest } from "../../utils/httpClient";

import { API_BASE } from "../../config/api";

import { useSimplifyModals } from "../../hooks/useSimplifyModals";


import { authService } from "../../services/auth";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { simplifyService } from "../../services/simplify";

import { useCurateBridge } from "../../hooks/useCurateBridge";
import { useGamePreviewHandlers } from "../../hooks/useGamePreviewHandlers";

import { useSimplifyPreferences, TIME_OPTIONS, ENERGY_OPTIONS, FEEL_OPTIONS } from "../../hooks/useSimplifyPreferences";

import { useSimplifyGames } from "../../hooks/useSimplifyGames";

import { useSimplifyCollection } from "../../hooks/useSimplifyCollection";

import { useSimplifySession } from "../../hooks/useSimplifySession";
import { usePlayList } from "../../hooks/usePlayList";


import SimplifyPreferencesPanel from "./SimplifyPreferencesPanel";
import SimplifyResultsPanel from "./SimplifyResultsPanel";
import SimplifySearchPanel from "./SimplifySearchPanel";
import SimplifyModalsComponent from "./SimplifyModals";
import SimplifyInfoModal from "./SimplifyInfoModal";
import AddGameModal from "./AddGameModal";
import { getGameOwnershipStatus, isOwnedStatus } from "../../utils/gameOwnership";

// Sidebar components reused from Collection
import CurateSimplified from "./CurateSimplified";
import Marketplace from "./Marketplace";
import PlayListSidebar from "./PlayListSidebar";

import "../../styles/quickdecision.css";
import logger from '../../utils/logger';



const Simplify = ({ user, onAddToSearch }) => {
  const { trackClick } = useAnalytics();
  const { setCurateRef, showSimilarGames, showComparison, resetCurate } = useCurateBridge();

  const location = useLocation();

  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);



  // Simplify hooks for state management
  const {
    // Modals
    showSimplifyModal,
    setShowSimplifyModal,
    showCurateSidebar,
    setShowCurateSidebar,
    previewGame,
    setPreviewGame,
    loadingPreview,
    setLoadingPreview,
    marketplaceGame,
    setMarketplaceGame,
    watchGame,
    setWatchGame,
    reminderGame,
    setReminderGame,
    showSuccessBanner,
    successBannerMessage
  } = useSimplifyModals();

  const {
    // Session
    setCurrentSession,
    showGameSelectionModal,
    setShowGameSelectionModal,
    showLetSPlayModal,
    setShowLetSPlayModal,
    letSPlayGame,
    setLetSPlayGame,
    showSessionFormInLetSPlay,
    setShowSessionFormInLetSPlay,
    showRulesSummaryInLetSPlay,
    setShowRulesSummaryInLetSPlay,
    showScorePadInLetSPlay,
    setShowScorePadInLetSPlay,
    showSessionModal,
    setShowSessionModal,
    sessionData,
    setSessionData,
    userMap,
    resetSessionState,
    fetchUserInfo,
    handleLogSession,
    handleOpenScorePad
  } = useSimplifySession(user);

  // Step management
  const [currentStep, setCurrentStep] = useState('preferences'); // 'preferences' or 'games'

  useLayoutEffect(() => {
    const appContent = document.querySelector(".app-content");
    if (appContent) {
      appContent.scrollTop = 0;
    }
    if (window.scrollTo) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, [currentStep]);

  // Loading state for recommendations
  const [isSearching, setIsSearching] = useState(false);

  // Banner state for notifications
  const [banner, setBanner] = useState({ show: false, message: '', type: 'success', linkTo: '/past-sessions' });

  // Legacy state (used by child components)
  const [gameSelectionMode] = useState(null);
  const [gameSelectionData] = useState(null);

  // Helper functions
  const handlePreferencesNext = () => {
    trackClick('jet_set_go_preferences', { preferences });
    setCurrentStep('games');
  };

  // Banner notification function for SessionForm - wrapped in useCallback
  // linkTo: '/past-sessions' for sessions, '/collection' for collection additions
  const showBanner = useCallback((message, type = 'success', linkTo = '/past-sessions') => {
    setBanner({ show: true, message, type, linkTo });
    // Auto-hide after 5 seconds
    setTimeout(() => setBanner({ show: false, message: '', type: 'success', linkTo: '/past-sessions' }), 5000);
  }, []);

  // Collection hook (must be after showBanner)
  const {
    collection,
    checkHasOwnedGames,
    loadCollection,
    isInCollection
  } = useSimplifyCollection(navigate, showBanner);

  const getGameOwnershipStatusForGame = useCallback(
    (gameId) => getGameOwnershipStatus(collection, gameId),
    [collection]
  );

  // Derive owned-game count from the loaded collection so the Jet Set Go counts
  // and ownership badges stay consistent with the actual collection data.
  const collectionOwnedCount = useMemo(
    () =>
      Array.isArray(collection)
        ? collection.filter((g) => isOwnedStatus(g.ownership_status)).length
        : 0,
    [collection]
  );

  // Play List state
  const {
    playList,
    count: playListCount,
    loading: playListLoading,
    addToPlayList,
    removeFromPlayList,
    clearPlayList,
    isInPlayList,
  } = usePlayList(user);
  const [showPlayListSidebar, setShowPlayListSidebar] = useState(false);

  const saveSession = useCallback((response) => {
    // SessionForm handles the API call; this just handles UI state cleanup on success
    if (response?.ok || response?.success) {
      trackClick('play_log', { game_name: response?.game_name, game_id: response?.chosen_game_id, source: 'simplify' });
      trackClick('session_logged', { game_name: response?.game_name, game_id: response?.chosen_game_id, source: 'simplify' });
      const message = response?.removed_from_playlist
        ? 'Session saved successfully! Game removed from Play List.'
        : 'Session saved successfully!';
      showBanner(message, 'success');
      setShowSessionFormInLetSPlay(false);
      setShowLetSPlayModal(false);
      resetSessionState();
    } else {
      showBanner('Failed to save session', 'error');
    }
  }, [setShowSessionFormInLetSPlay, setShowLetSPlayModal, resetSessionState, showBanner, trackClick]);


  // Constants
  const PLAYER_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8];

  // Update tab when URL parameter changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    // Show modal when on Simplify for first-time visit
    if (!currentTab || currentTab === 'simplify') {
      const hasSeenModal = localStorage.getItem('simplifyModalSeen');
      if (!hasSeenModal) {
        setShowSimplifyModal(true);
        localStorage.setItem('simplifyModalSeen', 'true');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);




  // Simplify preferences hook
  const {
    preferences,
    setPreferences,
    setFamiliarity,
    setFeel
  } = useSimplifyPreferences();

  // Pre-populate player count from navigation state (e.g. from insight CTA)
  useEffect(() => {
    const statePlayers = location.state?.players;
    if (statePlayers && Number.isInteger(statePlayers) && statePlayers >= 1 && statePlayers <= 8) {
      setPreferences(prev => ({ ...prev, players: statePlayers }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simplify games hook
  const {
    selectedGames,
    setSelectedGames,
    selectGame,
    removeGame,
    clearGames,
    gameSearchQuery,
    setGameSearchQuery,
    gameSearchResults,
    setGameSearchResults,
    showGameDropdown,
    setShowGameDropdown,
    isGameSearchLoading,
    setIsGameSearchLoading,
    recommendations,
    setRecommendations,
    disqualifiedGames,
    setDisqualifiedGames,
    clearRecommendations,
    loadingCollection,
    findInCollection,
    getRecommendations
  } = useSimplifyGames();

  // Add Game Modal state
  const [addGameModalGame, setAddGameModalGame] = useState(null);
  const [showAddGameModal, setShowAddGameModal] = useState(false);

  // Handler to open Add Game Modal
  const handleOpenAddGameModal = useCallback((game) => {
    setAddGameModalGame(game);
    setShowAddGameModal(true);
  }, []);

  // Handler to close Add Game Modal
  const handleCloseAddGameModal = useCallback(() => {
    setShowAddGameModal(false);
    setAddGameModalGame(null);
  }, []);

  // Handler when game is saved from modal
  const handleSaveGameFromModal = useCallback(async () => {
    // AddGameModal already made the API call with full data (preference, ownership, tags)
    // Just reload the collection to reflect the changes
    await loadCollection();
    showBanner('Game added to library', 'success', '/collection');
  }, [loadCollection, showBanner]);

  // Handler when game is excluded (Not for me) - refresh collection to update UI
  const handleExcludeGame = useCallback(async () => {
    await loadCollection();
  }, [loadCollection]);

  // Play List toggle handler for game tiles
  const handleTogglePlayList = useCallback(async (game) => {
    const gameId = game.game_id || game.id;
    if (isInPlayList(gameId)) {
      await removeFromPlayList(gameId);
    } else {
      await addToPlayList(game);
      showBanner('Added to Play List', 'success', null);
    }
  }, [isInPlayList, addToPlayList, removeFromPlayList, showBanner]);

  // Add all Play List games to the current Simplify selection
  const handleAddFromPlayList = useCallback(() => {
    playList.forEach((game) => {
      const gameId = game.game_id || game.id;
      if (!selectedGames.some((g) => (g.id === gameId || g.game_id === gameId))) {
        selectGame({
          id: gameId,
          game_id: gameId,
          name: game.name,
          thumbnail: game.thumbnail,
          year_published: game.year_published,
        });
      }
    });
  }, [playList, selectedGames, selectGame]);


  const checkExistingSession = useCallback(async () => {

    try {

      const response = await simplifyService.getLatestSession();

      if (response.session && !response.session.completed) {

        setCurrentSession(response.session);

        // Show game selection modal if user hasn't chosen a game yet

        if (!response.session.chosen_game_id) {
          setShowGameSelectionModal(true);
        }

      }

    } catch (error) {

      logger.error("Error checking existing session:", error);

    }

  }, [setCurrentSession, setShowGameSelectionModal]);

  // Check for existing Simplify session on component mount

  useEffect(() => {

    if (user) {

      checkExistingSession();

    }

  }, [checkExistingSession, user]);



  // Handle "Log session" button click in modal - wraps hook's handleLogSession
  const handleLogSessionClick = useCallback((game) => {
    const targetGame = game || letSPlayGame;
    if (!targetGame) return;

    // Ensure letSPlayGame is set (in case called from different context)
    if (!letSPlayGame) {
      setLetSPlayGame(targetGame);
    }

    // Use the hook's handleLogSession after ensuring game is set
    setTimeout(() => {
      handleLogSession();
    }, 0);
  }, [letSPlayGame, setLetSPlayGame, handleLogSession]);

  const handleLaunchSessionFromAddGame = useCallback((savedGame) => {
    if (!savedGame) return;
    handleLogSessionClick(savedGame);
  }, [handleLogSessionClick]);



  // Game preview handlers using unified hook
  const {
    handleSearchSimilar,
    handleCompare,
    handleAddToMarketplace,
    handleAddWatch,
    handleAddReminder,
    handleLetSPlay,
  } = useGamePreviewHandlers({
    setPreviewGame,
    setShowCurateSidebar,
    setMarketplaceGame,
    setReminderGame,
    curateBridge: { showSimilarGames, showComparison, resetCurate },
    showBanner,
    setWatchGame,
    setLetSPlayGame,
    setShowLetSPlayModal,
  });

  // Handle "See rules summary" button click in modal
  const handleSeeRulesSummary = () => {

    if (!letSPlayGame) return;



    // Log for analytics




    // Show rules summary inline in the same modal

    setShowRulesSummaryInLetSPlay(true);

  };

















  // Handle game selection from modal
  const handleGameSelection = (game) => {
    setShowGameSelectionModal(false);
    setPreviewGame(game);
  };


















  // Handle return URL after login

  useEffect(() => {

    if (user) {

      const urlParams = new URLSearchParams(window.location.search);

      const returnTo = urlParams.get('returnTo');



      if (returnTo) {

        // Clear the returnTo parameter

        const newUrl = window.location.pathname;

        window.history.replaceState({}, '', newUrl);



        // If we have a letSPlayGame stored, open the session modal

        if (letSPlayGame) {

          setSessionData({

            game: letSPlayGame,

            date: new Date().toISOString().split('T')[0],

            guilds: [],

            familiars: [],

            scores: {}

          });

          setShowSessionModal(true);

          setShowLetSPlayModal(false);

        }

      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, letSPlayGame]);

  // Update game familiarity
  const updateGameFamiliarity = useCallback((gameId, familiarityLevel) => {
    setSelectedGames(prev => prev.map(game =>
      game.id === gameId
        ? { ...game, isFamiliar: familiarityLevel > 0 }
        : game
    ));
  }, [setSelectedGames]);

  // Handle find in collection - wrapper around hook function
  const handleFindInCollection = useCallback(async () => {
    if (!user) return;
    const result = await findInCollection(preferences);
    if (result) {
      setRecommendations(result.recommendations);
      setDisqualifiedGames(result.disqualified);
      setCurrentStep('results');
    }
  }, [preferences, findInCollection, setRecommendations, setDisqualifiedGames, setCurrentStep, user]);

  // Check if user has owned games when user logs in — lightweight count call first,
  // then load the full collection so ownership badges can resolve without racing the toggle state.
  React.useEffect(() => {
    if (user) {
      (async () => {
        await checkHasOwnedGames();
        await loadCollection();
      })();
    }
  }, [user, checkHasOwnedGames, loadCollection]);



  // Fetch full game details for preview

  const fetchGameDetails = useCallback(async (game) => {

    if (!game || !game.id) return;

    setLoadingPreview(true);

    try {

      const res = await httpRequest(`${API_BASE}/games/${game.id}/details`, {

        method: "GET",

        headers: authService.getAuthHeaders(),

      });

      if (res.ok) {

        const gameDetails = await res.json();

        setPreviewGame({ ...game, ...gameDetails });

      } else {

        setPreviewGame(game);

      }

    } catch (err) {

      logger.error("Failed to fetch game details:", err);

      setPreviewGame(game);

    } finally {

      setLoadingPreview(false);

    }

  }, [setPreviewGame, setLoadingPreview]);



  // Search for games

  const searchGames = useCallback(async (query) => {

    if (query.length < 2) {

      setGameSearchResults([]);

      setShowGameDropdown(false);

      return;

    }

    setIsGameSearchLoading(true);

    setShowGameDropdown(true);

    try {

      const res = await httpRequest(`${API_BASE}/games/search?q=${encodeURIComponent(query)}&limit=10`, {

        method: "GET"

      });

      if (res.ok) {

        const data = await res.json();

        setGameSearchResults(data.games || []);

      } else {

        setGameSearchResults([]);

      }

    } catch (err) {

      logger.error("Failed to search games:", err);

      setGameSearchResults([]);

    } finally {

      setIsGameSearchLoading(false);

    }
  }, [setIsGameSearchLoading, setGameSearchResults, setShowGameDropdown]);

  // Get recommendations - score and rank selected games using backend batch scoring
  const handleGetRecommendations = useCallback(async () => {
    if (selectedGames.length === 0) return;
    trackClick('jet_set_go_recommendations', { game_count: selectedGames.length, preferences });

    try {
      setIsSearching(true);

      // Extract game IDs from selected games
      const gameIds = selectedGames.map(g => g.id);

      // Use backend batch scoring (clean break from local rankGames)
      // Backend will fetch game details from DB
      const { recommendations, disqualified } = await getRecommendations(
        gameIds,
        preferences
      );

      setRecommendations(recommendations);
      setDisqualifiedGames(disqualified);

      // Navigate to results step to show scored games
      setCurrentStep('results');

    } catch (err) {
      logger.error('Error getting recommendations:', err);
      // Could show error toast here
    } finally {
      setIsSearching(false);
    }
  }, [selectedGames, preferences, getRecommendations, setRecommendations, setDisqualifiedGames, setCurrentStep, trackClick]);

  // ...

  return (
    <>
      {/* Main container */}
  <div className="simplify-container" style={{
      minHeight: "auto",
      height: "auto",
      overflow: "visible"
    }}>

      {/* Global Success Banner */}

      {showSuccessBanner && (

        <div

          style={{

            position: "fixed",

            top: "calc(20px + env(safe-area-inset-top, 0px))",

            left: "50%",

            transform: "translateX(-50%)",

            backgroundColor: "var(--success-color, #4caf50)",

            color: "white",

            padding: "1rem 1.5rem",

            borderRadius: "8px",

            fontSize: "1rem",

            fontWeight: "500",

            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",

            zIndex: 10000,

            animation: "slideDown 0.3s ease-out"

          }}

        >

          {successBannerMessage}

        </div>

      )}

      {/* SessionForm Notification Banner */}

      {banner.show && (

        <div

          style={{

            position: "fixed",

            top: "calc(80px + env(safe-area-inset-top, 0px))",

            left: "50%",

            transform: "translateX(-50%)",

            backgroundColor: banner.type === 'error' ? 'var(--error-color, #dc3545)' : banner.type === 'warning' ? 'var(--warning-color, #ffc107)' : 'var(--success-color, #4caf50)',

            color: banner.type === 'warning' ? 'var(--text-primary, #333)' : 'white',

            padding: "1rem 1.5rem",

            borderRadius: "8px",

            fontSize: "1rem",

            fontWeight: "500",

            zIndex: 10001,

            animation: "slideDown 0.3s ease-out"

          }}

        >

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'transparent' }}>

            {banner.message}

            {banner.type === 'success' && banner.linkTo && (

              <Link

                to={banner.linkTo}

                style={{

                  color: 'inherit',

                  textDecoration: 'underline',

                  fontSize: '0.9rem',

                  opacity: 0.9,

                  display: 'flex',

                  alignItems: 'center',

                  gap: '0.25rem'

                }}

              >

                {banner.linkTo === '/collection'
                  ? 'View your library'
                  : <>View your plays in {TIMELINE_ICON}</>}

              </Link>

            )}

          </div>

        </div>

      )}



      {/* Tab Content */}

      {/* Info Modal */}
      <SimplifyInfoModal
        show={showSimplifyModal}
        onClose={() => setShowSimplifyModal(false)}
      />

      {/* Preferences Panel */}
      {currentStep === 'preferences' && (
        <SimplifyPreferencesPanel
          preferences={preferences}
          setPreferences={setPreferences}
          setFamiliarity={setFamiliarity}
          setFeel={setFeel}
          playerCountOptions={PLAYER_COUNTS}
          timeOptions={TIME_OPTIONS}
          energyOptions={ENERGY_OPTIONS}
          feelOptions={FEEL_OPTIONS}
          onNext={handlePreferencesNext}
          onShowHelp={() => setShowSimplifyModal(true)}
          hasSelectedGames={selectedGames.length > 0}
        />
      )}

      {/* Game Selection Panel */}
      {currentStep === 'games' && (
        <SimplifySearchPanel
          selectedGames={selectedGames}
          setSelectedGames={setSelectedGames}
          gameSearchQuery={gameSearchQuery}
          setGameSearchQuery={setGameSearchQuery}
          gameSearchResults={gameSearchResults}
          setGameSearchResults={setGameSearchResults}
          showGameDropdown={showGameDropdown}
          setShowGameDropdown={setShowGameDropdown}
          isGameSearchLoading={isGameSearchLoading}
          setIsGameSearchLoading={setIsGameSearchLoading}
          onSelectGame={selectGame}
          onRemoveGame={removeGame}
          onClearAll={clearGames}
          onGetRecommendations={handleGetRecommendations}
          isSearching={isSearching}
          onSearchGames={searchGames}
          onToggleFamiliar={updateGameFamiliarity}
          onFindInCollection={handleFindInCollection}
          loadingCollection={loadingCollection}
          ownedCount={collectionOwnedCount}
          user={user}
          showGameSelectionBanner={false}
          bannerMessage=""
          onBack={() => setCurrentStep('preferences')}
          preferences={preferences}
          familiarity={preferences.familiarity}
          setFamiliarity={setFamiliarity}
          playListCount={playListCount}
          onAddFromPlayList={handleAddFromPlayList}
        />
      )}

      {/* Results Panel */}
      {currentStep === 'results' && (
        <SimplifyResultsPanel
          recommendations={recommendations}
          disqualifiedGames={disqualifiedGames}
          preferences={preferences}
          toCamelCase={(str) => str.replace(/\b\w/g, l => l.toUpperCase())}
          TIME_OPTIONS={TIME_OPTIONS}
          onAddToSearch={onAddToSearch}
          fetchGameDetails={fetchGameDetails}
          isInCollection={isInCollection}
          openAddToCollectionModal={handleOpenAddGameModal}
          handleLetSPlay={(game) => {
            setLetSPlayGame(game);
            setShowLetSPlayModal(true);
          }}
          onNavigateBack={() => setCurrentStep('games')}
          onReset={() => {
            clearGames();
            clearRecommendations();
            setCurrentStep('preferences');
          }}
          setSelectedGames={setSelectedGames}
          setRecommendations={setRecommendations}
          onSearchSimilar={handleSearchSimilar}
          onAddToMarketplace={handleAddToMarketplace}
          onAddReminder={handleAddReminder}
          onCompareGames={handleCompare}
          user={user}
          collection={collection}
          onTogglePlayList={handleTogglePlayList}
          inPlayList={isInPlayList}
          onExcludeGame={handleExcludeGame}
        />
      )}



      {/* Curate Sidebar — CurateSimplified renders its own SidebarShell; always mounted so ref stays valid */}
      <CurateSimplified
        ref={setCurateRef}
        isOpen={showCurateSidebar}
        user={user}
        onClose={() => setShowCurateSidebar(false)}
        sidebarMode={true}
        getGameOwnershipStatus={getGameOwnershipStatusForGame}
      />

      {/* Play List Sidebar */}
      <PlayListSidebar
        isOpen={showPlayListSidebar}
        onClose={() => setShowPlayListSidebar(false)}
        playList={playList}
        loading={playListLoading}
        onRemove={removeFromPlayList}
        onClear={clearPlayList}
      />



      {/* Modals */}

      <SimplifyModalsComponent
        previewGame={previewGame}
        setPreviewGame={setPreviewGame}
        watchGame={watchGame}
        setWatchGame={setWatchGame}
        reminderGame={reminderGame}
        setReminderGame={setReminderGame}
        showGameSelectionModal={showGameSelectionModal}
        setShowGameSelectionModal={setShowGameSelectionModal}
        gameSelectionMode={gameSelectionMode}
        gameSelectionData={gameSelectionData}
        onSelectGame={handleGameSelection}
        showLetSPlayModal={showLetSPlayModal}
        setShowLetSPlayModal={setShowLetSPlayModal}
        letSPlayGame={letSPlayGame}
        setLetSPlayGame={setLetSPlayGame}
        showSessionFormInLetSPlay={showSessionFormInLetSPlay}
        setShowSessionFormInLetSPlay={setShowSessionFormInLetSPlay}
        showRulesSummaryInLetSPlay={showRulesSummaryInLetSPlay}
        setShowRulesSummaryInLetSPlay={setShowRulesSummaryInLetSPlay}
        showScorePadInLetSPlay={showScorePadInLetSPlay}
        setShowScorePadInLetSPlay={setShowScorePadInLetSPlay}
        onLogSession={handleLogSessionClick}
        onSeeRulesSummary={handleSeeRulesSummary}
        onOpenScorePad={handleOpenScorePad}
        sessionData={sessionData}
        setSessionData={setSessionData}
        onSaveSession={saveSession}
        showSessionModal={showSessionModal}
        user={user}
        userMap={userMap}
        fetchUserInfo={fetchUserInfo}
        showBanner={showBanner}
        onSearchSimilar={handleSearchSimilar}
        onCompare={handleCompare}
        onAddToMarketplace={handleAddToMarketplace}
        onAddWatch={handleAddWatch}
        onAddReminder={handleAddReminder}
        onLetSPlay={handleLetSPlay}
        onTogglePlayList={handleTogglePlayList}
        inPlayList={isInPlayList}
      />

      {/* Marketplace Modal */}
      {marketplaceGame && (
        <Marketplace
          gameId={marketplaceGame.id}
          gameName={marketplaceGame.name}
          onClose={() => setMarketplaceGame(null)}
        />
      )}

      {/* Add Game Modal */}
      <AddGameModal
        game={addGameModalGame}
        isOpen={showAddGameModal}
        onClose={handleCloseAddGameModal}
        onSave={handleSaveGameFromModal}
        onLaunchSession={handleLaunchSessionFromAddGame}
        user={user}
      />

      {/* Loading overlay for game details */}

      {loadingPreview && (

        <div

          style={{

            position: "fixed",

            top: 0,

            left: 0,

            right: 0,

            bottom: 0,

            backgroundColor: "rgba(0, 0, 0, 0.5)",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            zIndex: 1001

          }}

        >

          <div

            style={{

              backgroundColor: "white",

              borderRadius: "8px",

              padding: "2rem",

              textAlign: "center"

            }}

          >

            <div style={{

              width: "40px",

              height: "40px",

              border: "4px solid var(--bg-tertiary, #f3f3f3)",

              borderTop: "4px solid var(--primary-color, #1976d2)",

              borderRadius: "50%",

              animation: "spin 1s linear infinite",

              margin: "0 auto 1rem"

            }} />

            <p style={{ margin: 0, color: "var(--text-primary, #333)" }}>Loading game details...</p>

          </div>

        </div>

      )}



    </div>

    </>
  );

};

export default Simplify;
